import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Card,
} from "react-bootstrap";
import {
  FaUser,
  FaPhone,
  FaBuilding,
  FaEnvelope,
  FaLock,
  FaTrash,
  FaStar,
  FaEye, 
  FaEyeSlash,
  FaInfoCircle,
  FaMapMarkerAlt,
  FaFileInvoice,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import NavbarMenu from "../heroSection/NavbarMenu";
import "bootstrap/dist/css/bootstrap.min.css";
import Footer from "../footer/Footer";
import { authAPI, userAPI } from "../../api/apiSystem";
import "./clientinfo.css";
import ClientChatBox from "../chat/ClientChatBox";

export default function ClientInfoForm() {
  const { t } = useTranslation();
  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // 🔹 SweetAlert Configuration
  const swalConfig = {
    confirmButtonColor: "#ff6600",
    cancelButtonColor: "#6c757d",
    timer: 4000,
  };

  // 🔹 SweetAlert Helpers
  const showAlert = {
    // Loading alert
    loading: (title) => {
      Swal.fire({
        title,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        ...swalConfig,
      });
    },

    // Success alert
    success: (title, text = "") => {
      Swal.fire({
        icon: "success",
        title,
        text,
        ...swalConfig,
        timer: 3000,
      });
    },

    // Error alert
    error: (title, text = "") => {
      Swal.fire({
        icon: "error",
        title,
        text,
        ...swalConfig,
      });
    },

    // Warning alert with confirmation
    warning: (title, text, confirmText, cancelText) => {
      return Swal.fire({
        icon: "warning",
        title,
        text,
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        reverseButtons: true,
      });
    },

    // Validation error alert with list
    validationError: (title, errors) => {
      const html = Array.isArray(errors)
        ? errors.map((error) => `• ${error}`).join("<br>")
        : `• ${errors}`;

      Swal.fire({
        icon: "error",
        title,
        html,
        ...swalConfig,
      });
    },

    // Close any open alert
    close: () => Swal.close(),
  };

  // States
  const [formData, setFormData] = useState({
    parent_id: null,
    parent: null,
    photo: "",
    title: "",
    firstname: "",
    company_name: "",
    lastname: "",
    nicname: "",
    email: "",
    street_address: "",
    addressone: "",
    postal_code: "",
    telphone: "",
    tax_number: "",
    local_court: "",
    responsible_person: "",
    email_checked: true,
  });

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [deleteData, setDeleteData] = useState({
    password: "",
    deleted_reason: "",
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [deleteErrors, setDeleteErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    new_password_confirmation: false,
  });

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: "", color: "#e5e7eb" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2)
      return { level: 1, label: t("common.weak", "Weak"), color: "#dc2626" };
    if (score === 3)
      return { level: 2, label: t("common.medium", "Medium"), color: "#d97706" };
    if (score === 4)
      return { level: 3, label: t("common.strong", "Strong"), color: "#16a34a" };
    return { level: 4, label: t("common.veryStrong", "Very strong"), color: "#059669" };
  };

  const convertTitleToEnglish = (germanTitle) => {
    const titleMap = { Herr: "Mr", Frau: "Ms", Divers: "Mr" };
    return titleMap[germanTitle] || "Mr";
  };

  const convertTitleToGerman = (englishTitle) => {
    const titleMap = { Mr: "Herr", Ms: "Frau" };
    return titleMap[englishTitle] || "Herr";
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.success) {
        const profile = response.data.data;

        const getValue = (value, defaultValue = "") => {
          return value !== null && value !== undefined ? value : defaultValue;
        };

        setFormData({
          parent_id: profile.parent_id,
          parent: profile.parent || null,
          title: convertTitleToGerman(profile.title) || "Herr",
          photo: getValue(profile.photo),
          firstname: getValue(profile.firstname),
          company_name: getValue(profile.company_name),
          lastname: getValue(profile.lastname),
          nicname: getValue(profile.nicname),           // ✅ الصح
          email: getValue(profile.email),
          street_address: getValue(profile.street_address),
          addressone: getValue(profile.addressone),
          postal_code: getValue(profile.postal_code),
          telphone: getValue(profile.telphone),
          tax_number: getValue(profile.tax_number),
          local_court: getValue(profile.local_court),
          responsible_person: getValue(profile.responsible_person),
          average_rating: getValue(profile.average_rating, 0),
          total_reviews: getValue(profile.total_reviews, 0),
          email_checked: Boolean(profile.email_checked),
        });

        // ✅ حدّث photoPreview لو في صورة موجودة
        if (profile.photo) {
          setPhotoPreview(profile.photo);
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      showAlert.error(t("alerts.error"), t("errors.loadProfile"));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.firstname.trim())
      errors.push(t("validation.firstNameRequired"));
    if (!formData.lastname.trim())
      errors.push(t("validation.lastNameRequired"));
    if (!formData.street_address.trim())
      errors.push(t("validation.streetAddressRequired"));
    if (!formData.postal_code.trim())
      errors.push(t("validation.postalCodeRequired"));
    if (!formData.telphone.trim()) errors.push(t("validation.phoneRequired"));
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.push(t("validation.invalidEmail"));
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showAlert.validationError(t("alerts.validationError"), validationErrors);
      return;
    }

    setLoading(true);
    showAlert.loading(t("common.saving"));

    try {
      // ✅ استخدام FormData لإرسال الصورة + الحقول
      const submitData = new FormData();

      // الحقول النصية
      submitData.append("title", convertTitleToEnglish(formData.title));
      submitData.append("firstname", formData.firstname || "");
      submitData.append("lastname", formData.lastname || "");
      submitData.append("company_name", formData.company_name || "");
      submitData.append("nicname", formData.nicname || "");       // ✅
      submitData.append("email", formData.email || "");
      submitData.append("street_address", formData.street_address || "");
      submitData.append("addressone", formData.addressone || "");
      submitData.append("postal_code", formData.postal_code || "");
      submitData.append("telphone", formData.telphone || "");
      submitData.append("tax_number", formData.tax_number || "");
      submitData.append("local_court", formData.local_court || "");
      submitData.append(
        "responsible_person",
        formData.responsible_person || ""
      );
      submitData.append("email_checked", formData.email_checked ? 1 : 0);

      // ✅ إضافة الصورة (لو موجودة)
      if (photoFile) {
        submitData.append("photo", photoFile);
      }

      const response = await userAPI.updateProfile(submitData);

      showAlert.close();

      if (response.success) {
        showAlert.success(t("alerts.success"), t("alerts.profileUpdated"));

        // ✅ تحديث الـ preview بعد الحفظ
        if (response.data?.data?.photo) {
          setPhotoPreview(response.data.data.photo);
          setFormData((prev) => ({
            ...prev,
            photo: response.data.data.photo,
          }));
        }

        // ✅ تصفير الـ file (لأنه اترفع)
        setPhotoFile(null);
      } else {
        showAlert.error(
          t("alerts.error"),
          response.message || t("errors.updateProfile")
        );
      }
    } catch (error) {
      console.error("Update error:", error);
      showAlert.close();
      showAlert.error(
        t("alerts.error"),
        error?.response?.data?.message || t("errors.updateError")
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordData.old_password.trim())
      errors.old_password = t("validation.oldPasswordRequired");
    if (!passwordData.new_password.trim())
      errors.new_password = t("validation.newPasswordRequired");
    else if (passwordData.new_password.length < 6)
      errors.new_password = t("validation.passwordMinLength");
    if (!passwordData.new_password_confirmation.trim())
      errors.new_password_confirmation = t(
        "validation.confirmPasswordRequired",
      );
    else if (
      passwordData.new_password !== passwordData.new_password_confirmation
    )
      errors.new_password_confirmation = t("validation.passwordsDoNotMatch");
    return errors;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validatePasswordForm();
    if (Object.keys(validationErrors).length > 0) {
      setPasswordErrors(validationErrors);

      // Show validation errors in SweetAlert
      const errorMessages = Object.values(validationErrors);
      showAlert.validationError(t("alerts.validationError"), errorMessages);
      return;
    }

    setPasswordLoading(true);
    setPasswordErrors({});

    showAlert.loading(t("common.changing"));

    try {
      const response = await authAPI.changePassword(passwordData);

      showAlert.close();

      if (response.success) {
        showAlert.success(t("alerts.success"), t("alerts.passwordChanged"));
        setPasswordData({
          old_password: "",
          new_password: "",
          new_password_confirmation: "",
        });
      } else {
        showAlert.error(
          t("alerts.error"),
          response.message || t("errors.changePassword"),
        );
      }
    } catch (error) {
      showAlert.close();
      showAlert.error(t("alerts.error"), t("errors.changePasswordError"));
    } finally {
      setPasswordLoading(false);
    }
  };

  // وظيفة حذف الحساب
  const handleDeleteChange = (e) => {
    const { name, value } = e.target;
    setDeleteData((prev) => ({ ...prev, [name]: value }));
    if (deleteErrors[name]) {
      setDeleteErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    // التحقق من كلمة المرور
    if (!deleteData.password.trim()) {
      setDeleteErrors({ password: t("validation.passwordRequiredForDelete") });
      showAlert.error(
        t("alerts.error"),
        t("validation.passwordRequiredForDelete"),
      );
      return;
    }

    // عرض نافذة تأكيد
    const result = await showAlert.warning(
      t("clientInfo.deleteAccountConfirmTitle"),
      t("clientInfo.deleteAccountConfirmText"),
      t("actions.yesDelete"),
      t("actions.cancel"),
    );

    if (!result.isConfirmed) {
      return;
    }

    setDeleteLoading(true);
    setDeleteErrors({});

    try {
      const response = await authAPI.deleteAccount(deleteData);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("alerts.success"),
          html:
            t("alerts.accountDeletedSuccessfully") ||
            "Ihr Konto wurde erfolgreich gelöscht. Vielen Dank, dass Sie TransCiroX genutzt haben.",
          confirmButtonColor: "#ff6600",
        }).then(() => {
          // تسجيل الخروج وإعادة التوجيه
          localStorage.removeItem("token");
          localStorage.removeItem("temp_token");
          window.location.href = "/";
        });
      } else {
        showAlert.error(
          t("alerts.error"),
          response.message || t("errors.deleteAccountError"),
        );
      }
    } catch (error) {
      showAlert.error(
        t("alerts.error"),
        error.message || t("errors.deleteAccountError"),
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4) return "#0d9f6e";      // أخضر
    if (score >= 3) return "#d97706";      // برتقالي
    return "#dc2626";                       // أحمر
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" variant="warning" />
        <span className="ms-2">{t("common.loading")}</span>
      </div>
    );
  }

  const overall = formData.average_rating ?? formData.rating ?? 0;
  const totalReviews = formData.total_reviews ?? 0;
  const overallColor = getScoreColor(overall);

  return (
    <>
      <NavbarMenu />
      <Container
        fluid
        className="client-info-section py-5 d-flex justify-content-center"
      >
        <Container
          className="card"
          style={{
            borderRadius: "12px",
            padding: "25px",
          }}
        >
          {/* 🔹 Tabs Navigation */}
          <Tabs
            defaultActiveKey="info"
            id="client-info-tabs"
            className="custom-tabs mb-4 fw-bold justify-content-center"
            fill
          >
            {/* 🧩 Tab 1: Client Information */}
            <Tab
              eventKey="info"
              title={
                <span className="tab-title">
                  <FaUser className="me-2" />
                  {t("clientInfo.tabInformation")}
                </span>
              }
            >
              <Form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="mb-4">
                  <h5 className="d-flex align-items-center mb-3">
                    <FaUser className="me-2 text-warning" size={22} />
                    {t("clientInfo.basicInformation")}
                  </h5>

                  {/* ✅ عرض بيانات الـ Parent - مع فحص آمن كامل */}
                  {formData?.parent_id && formData?.parent && (() => {
                    const parent = formData.parent;
                    
                    return (
                      <div
                        className="alert alert-info border-0 shadow-sm mb-3"
                        role="alert"
                        style={{ borderRadius: 12 }}
                      >
                        <div className="d-flex align-items-start gap-3">
                          {/* أيقونة */}
                          <div
                            className="d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{
                              width: 42,
                              height: 42,
                              borderRadius: "50%",
                              background: "#0dcaf0",
                              color: "#fff",
                            }}
                          >
                            <FaInfoCircle size={20} />
                          </div>

                          <div className="flex-grow-1">
                            {/* العنوان */}
                            <h6 className="fw-bold mb-3" style={{ color: "#055160" }}>
                              {t("clientInfo.parentIdInfo", "Übergeordnetes Konto")}
                            </h6>

                            {/* بيانات الـ Parent */}
                            <div className="row g-3">
                              {/* الاسم */}
                              <div className="col-md-6">
                                <div className="d-flex align-items-center gap-2">
                                  <FaUser size={14} className="text-muted flex-shrink-0" />
                                  <div>
                                    <div
                                      className="text-muted"
                                      style={{ fontSize: 11, lineHeight: 1.2 }}
                                    >
                                      {t("clientInfo.parentName", "Name")}
                                    </div>
                                    <div className="fw-semibold" style={{ fontSize: 14 }}>
                                      {parent?.title ? `${parent.title}. ` : ""}
                                      {parent?.firstname ?? "-"} {parent?.lastname ?? ""}
                                      {parent?.nicname && (
                                        <span className="text-muted ms-1">
                                          ({parent.nicname})
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* الشركة */}
                              {parent?.company_name && (
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center gap-2">
                                    <FaBuilding size={14} className="text-muted flex-shrink-0" />
                                    <div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11, lineHeight: 1.2 }}
                                      >
                                        {t("clientInfo.parentCompany", "Firma")}
                                      </div>
                                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                                        {parent.company_name}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* الإيميل */}
                              {parent?.email && (
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center gap-2">
                                    <FaEnvelope size={14} className="text-muted flex-shrink-0" />
                                    <div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11, lineHeight: 1.2 }}
                                      >
                                        {t("clientInfo.parentEmail", "E-Mail")}
                                      </div>
                                      <a
                                        href={`mailto:${parent.email}`}
                                        className="fw-semibold text-decoration-none"
                                        style={{ fontSize: 14, color: "#0d6efd" }}
                                      >
                                        {parent.email}
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* الهاتف */}
                              {parent?.telphone && (
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center gap-2">
                                    <FaPhone size={14} className="text-muted flex-shrink-0" />
                                    <div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11, lineHeight: 1.2 }}
                                      >
                                        {t("clientInfo.parentPhone", "Telefon")}
                                      </div>
                                      <a
                                        href={`tel:${parent.telphone}`}
                                        className="fw-semibold text-decoration-none"
                                        style={{ fontSize: 14, color: "#0d6efd" }}
                                        dir="ltr"
                                      >
                                        {parent.telphone}
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* العنوان */}
                              {(parent?.street_address || parent?.postal_code) && (
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center gap-2">
                                    <FaMapMarkerAlt
                                      size={14}
                                      className="text-muted flex-shrink-0"
                                    />
                                    <div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11, lineHeight: 1.2 }}
                                      >
                                        {t("clientInfo.parentAddress", "Adresse")}
                                      </div>
                                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                                        {parent?.street_address ?? ""}
                                        {parent?.addressone && (
                                          <>
                                            <br />
                                            <span className="text-muted" style={{ fontSize: 13 }}>
                                              {parent.addressone}
                                            </span>
                                          </>
                                        )}
                                        {parent?.postal_code && (
                                          <span className="text-muted ms-1">
                                            {parent.postal_code}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* الرقم الضريبي */}
                              {parent?.tax_number && (
                                <div className="col-md-6">
                                  <div className="d-flex align-items-center gap-2">
                                    <FaFileInvoice
                                      size={14}
                                      className="text-muted flex-shrink-0"
                                    />
                                    <div>
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11, lineHeight: 1.2 }}
                                      >
                                        {t("clientInfo.parentTaxNumber", "USt-IdNr")}
                                      </div>
                                      <div className="fw-semibold" style={{ fontSize: 14 }}>
                                        {parent.tax_number}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 36,
                          height: 36,
                          background: overallColor,
                          color: "#fff",
                        }}
                      >
                        <FaStar size={16} />
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: 14, color: "#101828" }}>
                          {t("rating.criteria.rating.title", "Kundenbewertung")}
                        </div>
                        {/* <div className="text-muted" style={{ fontSize: 11 }}>
                          {t("rating.display_subtitle", "Bewertung dieses Auftrags")}
                        </div> */}
                      </div>
                    </div>
            
                    <div className="d-flex gap-2">
                      {/* Overall score badge */}
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                        style={{ background: overallColor, color: "#fff" }}
                      >
                        <FaStar size={12} />
                        <span className="fw-bold" style={{ fontSize: 14 }}>
                          {overall.toFixed(1)}
                        </span>
                      </div>

                      {/* Total Reviews badge */}
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                        style={{ background: '#033b49', color: "#fff" }}
                      >
                        <span className="fw-bold" style={{ fontSize: 14, color: "#fff" }}>
                          {totalReviews}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Row className="g-3">
                    {/* 🔹 Salutation (Title) as Radio Buttons */}
                    <Col xs={12} md={6} className="mx-auto my-0">
                      <div className="text-center mb-3">
                        <p className="fw-bold">
                          {photoPreview || formData.photo
                            ? t("existing_photo", "Aktuelles Foto")
                            : t("no_photo", "Kein Foto")}
                        </p>

                        <div
                          className="rounded-circle d-inline-flex align-items-center justify-content-center overflow-hidden mx-auto"
                          style={{
                            width: 130,
                            height: 130,
                            background: photoPreview ? "transparent" : "#f3f4f6",
                            border: "3px solid #ff7b00",
                          }}
                        >
                          {photoPreview ? (
                            <img
                              src={photoPreview}
                              alt="Profile"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          ) : (
                            <FaUser size={50} style={{ color: "#9ca3af" }} />
                          )}
                        </div>
                      </div>

                      <Form.Group controlId="formPhotoFile" className="mb-3 text-center">
                        <Form.Label className="fw-bold">{t("new_photo", "Neues Foto")}</Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, setPhotoFile, setPhotoPreview)}
                        />
                        {photoFile && (
                          <Form.Text className="text-muted d-block mt-1">
                            {photoFile.name} ({(photoFile.size / 1024).toFixed(1)} KB)
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={12}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.salutation")} *
                        </Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            label={t("clientInfo.mr")}
                            className="text-dark"
                            name="title"
                            value="Herr"
                            checked={formData.title === "Herr"}
                            onChange={handleChange}
                            required
                          />
                          <Form.Check
                            type="radio"
                            label={t("clientInfo.ms")}
                            className="text-dark"
                            name="title"
                            value="Frau"
                            checked={formData.title === "Frau"}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={12}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.companyName")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="company_name"
                          placeholder={t("clientInfo.companyNamePlaceholder")}
                          value={formData.company_name}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.firstName")} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="firstname"
                          placeholder={t("clientInfo.firstNamePlaceholder")}
                          value={formData.firstname}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.lastName")} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="lastname"
                          placeholder={t("clientInfo.lastNamePlaceholder")}
                          value={formData.lastname}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.nickname")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="nicname"
                          placeholder={t("clientInfo.nicknamePlaceholder")}
                          value={formData.nicname}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.streetAddress")} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="street_address"
                          placeholder={t("clientInfo.streetAddressPlaceholder")}
                          value={formData.street_address}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.postalCode")} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="postal_code"
                          placeholder={t("clientInfo.postalCodePlaceholder")}
                          value={formData.postal_code}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.additionalAddress")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="addressone"
                          placeholder={t(
                            "clientInfo.additionalAddressPlaceholder",
                          )}
                          value={formData.addressone}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Contact Details */}
                <div className="mb-4">
                  <h5 className="d-flex align-items-center mb-3">
                    <FaPhone className="me-2 text-warning" size={22} />
                    {t("clientInfo.contactDetails")}
                  </h5>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.phone")} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="telphone"
                          placeholder={t("clientInfo.phonePlaceholder")}
                          value={formData.telphone}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.email")} *
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder={t("clientInfo.emailPlaceholder")}
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Commercial Information */}
                <div className="mb-4">
                  <h5 className="d-flex align-items-center mb-3">
                    <FaBuilding className="me-2 text-warning" size={22} />
                    {t("clientInfo.commercialInformation")}
                  </h5>
                  <Row className="g-3">
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.taxNumber")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="tax_number"
                          placeholder={t("clientInfo.taxNumberPlaceholder")}
                          value={formData.tax_number}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.localCourt")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="local_court"
                          placeholder={t("clientInfo.localCourtPlaceholder")}
                          value={formData.local_court}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          {t("clientInfo.responsiblePerson")}
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="responsible_person"
                          placeholder={t(
                            "clientInfo.responsiblePersonPlaceholder",
                          )}
                          value={formData.responsible_person}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                {/* Email Checkbox */}
                <div className="mb-4">
                  <Form.Group>
                    <Form.Check
                      type="checkbox"
                      name="email_checked"
                      label={t("clientInfo.useEmailForNotifications", {
                        email: formData.email,
                      })}
                      checked={formData.email_checked}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </div>

                {/* Save Button */}
                <div className="text-center">
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      backgroundColor: "#ff6600",
                      border: "none",
                      padding: "10px 40px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      borderRadius: "6px",
                      marginRight: "10px",
                    }}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t("common.saving")}
                      </>
                    ) : (
                      t("actions.save")
                    )}
                  </Button>
                </div>
              </Form>
            </Tab>

            {/* 🔒 Tab 2: Security */}
            <Tab
              eventKey="security"
              title={
                <span className="tab-title">
                  <FaLock className="me-2" />
                  {t("clientInfo.tabSecurity")}
                </span>
              }
            >
              <Card
                style={{
                  border: "0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <FaLock size={40} className="text-warning mb-3" />
                    <h5 className="fw-bold mb-3">
                      {t("clientInfo.changePassword")}
                    </h5>
                    <p className="text-muted mb-4">
                      {t("clientInfo.changePasswordDescription")}
                    </p>
                  </div>

                  <Form onSubmit={handlePasswordSubmit}>
                    <Row className="g-3">
                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            {t("clientInfo.oldPassword")} *
                          </Form.Label>

                          <div className="password-input-wrapper">
                            <Form.Control
                              type={showPasswords.old_password ? "text" : "password"}
                              name="old_password"
                              value={passwordData.old_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.old_password}
                              placeholder={t("clientInfo.oldPasswordPlaceholder")}
                              className="dark-bg-dark dark-text-white border-secondary password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("old_password")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.old_password
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.old_password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          <Form.Control.Feedback type="invalid">
                            {passwordErrors.old_password}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            {t("clientInfo.newPassword")} *
                          </Form.Label>
                          <div className="password-input-wrapper">
                            <Form.Control
                              type={showPasswords.new_password ? "text" : "password"}
                              name="new_password"
                              value={passwordData.new_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.new_password}
                              placeholder={t("clientInfo.newPasswordPlaceholder")}
                              className="dark-bg-dark dark-text-white border-secondary password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("new_password")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.new_password
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.new_password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          {passwordData.new_password && (
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-1">
                                <small className="text-muted">{t("common.passwordStrength", "Password strength")}</small>
                                <small style={{ color: getPasswordStrength(passwordData.new_password).color, fontWeight: 600 }}>
                                  {getPasswordStrength(passwordData.new_password).label}
                                </small>
                              </div>
                              <div className="password-strength-bar">
                                <div
                                  className="password-strength-fill"
                                  style={{
                                    width: `${(getPasswordStrength(passwordData.new_password).level / 4) * 100}%`,
                                    backgroundColor: getPasswordStrength(passwordData.new_password).color,
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          <Form.Control.Feedback type="invalid">
                            {passwordErrors.new_password}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            {t("clientInfo.confirmNewPassword")} *
                          </Form.Label>
                          <div className="password-input-wrapper">
                            <Form.Control
                              type={
                                showPasswords.new_password_confirmation ? "text" : "password"
                              }
                              name="new_password_confirmation"
                              value={passwordData.new_password_confirmation}
                              onChange={handlePasswordChange}
                              isInvalid={
                                !!passwordErrors.new_password_confirmation
                              }
                              placeholder={t(
                                "clientInfo.confirmPasswordPlaceholder",
                              )}
                              className="dark-bg-dark dark-text-white border-secondary password-input"
                              />

                              <button
                                type="button"
                                className="password-toggle-btn"
                                onClick={() => togglePassword("new_password_confirmation")}
                                tabIndex={-1}
                                aria-label={
                                  showPasswords.new_password_confirmation
                                    ? t("common.hidePassword", "Hide password")
                                    : t("common.showPassword", "Show password")
                                }
                              >
                                {showPasswords.new_password_confirmation ? (
                                  <FaEyeSlash />
                                ) : (
                                  <FaEye />
                                )}
                              </button>
                          </div>
                          
                          <Form.Control.Feedback type="invalid">
                            {passwordErrors.new_password_confirmation}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="text-center mt-4">
                      <Button
                        type="submit"
                        disabled={passwordLoading}
                        style={{
                          backgroundColor: "#ff6600",
                          border: "none",
                          fontWeight: "bold",
                          padding: "10px 35px",
                          borderRadius: "6px",
                        }}
                      >
                        {passwordLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            {t("common.changing")}
                          </>
                        ) : (
                          <>
                            <FaLock className="me-2" />
                            {t("actions.changePassword")}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* 🗑️ Tab 3: Delete Account */}
            <Tab
              eventKey="delete"
              title={
                <span className="tab-title">
                  <FaTrash className="me-2" />
                  {t("clientInfo.tabDeleteAccount")}
                </span>
              }
            >
              <Card
                style={{
                  border: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <FaTrash size={40} className="text-danger mb-3" />
                    <h5 className="fw-bold mb-3 text-danger">
                      {t("clientInfo.deleteAccount")}
                    </h5>
                    <p className="text-muted mb-4">
                      {t("clientInfo.deleteAccountWarning")}
                    </p>
                  </div>

                  <Form onSubmit={handleDeleteAccount}>
                    <Row className="g-3">
                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            {t("clientInfo.currentPassword")} *
                          </Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={deleteData.password}
                            onChange={handleDeleteChange}
                            isInvalid={!!deleteErrors.password}
                            placeholder={t(
                              "clientInfo.currentPasswordPlaceholder",
                            )}
                          />
                          <Form.Control.Feedback type="invalid">
                            {deleteErrors.password}
                          </Form.Control.Feedback>
                          <Form.Text className="text-muted">
                            {t("clientInfo.passwordRequiredForDelete")}
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold">
                            {t("clientInfo.deleteReason")}
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="deleted_reason"
                            value={deleteData.deleted_reason}
                            onChange={handleDeleteChange}
                            placeholder={t(
                              "clientInfo.deleteReasonPlaceholder",
                            )}
                          />
                          <Form.Text className="text-muted">
                            {t("clientInfo.deleteReasonHelp")}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="text-center mt-4">
                      <Button
                        type="submit"
                        disabled={deleteLoading}
                        style={{
                          backgroundColor: "#dc3545",
                          border: "none",
                          fontWeight: "bold",
                          padding: "10px 35px",
                          borderRadius: "6px",
                        }}
                      >
                        {deleteLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            {t("common.deleting")}
                          </>
                        ) : (
                          <>
                            <FaTrash className="me-2" />
                            {t("actions.deleteAccount")}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Container>
      </Container>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}
